/**
 * TagsContactCard — admin tags + contact log for one clinic, editable from
 * the detail page (same mutations as the clinics list: setNivClinicTagIds,
 * addNivCrmContact, deleteNivCrmContact). Reuses the list's popovers.
 */

import { useState } from "react";
import {
  ChatText,
  DotsThree,
  Envelope,
  Phone,
  Plus,
  Tag,
  Trash,
  WhatsappLogo,
} from "@phosphor-icons/react";
import HoverTip from "../../../../../components/ui/HoverTip";
import { useToast } from "../../../../../contexts/ToastContext";
import {
  useDeleteNivCrmContactMutation,
  useGetNivClinicTagsRegistryQuery,
} from "../../../../../store/services/nivApi";
import { UserTagChip } from "../../shared/ClinicUserTags";
import { CONTACT_CHANNEL_LABELS, formatDate, getRelativeDate } from "../../shared/nivDerived";
import TagAssignPopover from "../../clinics/TagAssignPopover";
import TagManagerModal from "../../clinics/TagManagerModal";
import ContactQuickLogPopover from "../../clinics/ContactQuickLogPopover";
import "../../clinics/NivClinicsTab.css";

const CHANNEL_ICONS = { call: Phone, whatsapp: WhatsappLogo, sms: ChatText, email: Envelope, other: DotsThree };

export default function TagsContactCard({ clinic, crm, author }) {
  const { showError } = useToast();
  const { data: registry } = useGetNivClinicTagsRegistryQuery();
  const [deleteContact, { isLoading: isDeleting }] = useDeleteNivCrmContactMutation();
  const [popover, setPopover] = useState(null); // "tags" | "contact"
  const [managerOpen, setManagerOpen] = useState(false);

  const tags = registry?.tags || [];
  const tagMap = Object.fromEntries(tags.map((t) => [t.id, t]));
  const tagIds = crm?.tagIds || [];
  const userTags = tagIds.map((id) => tagMap[id]).filter(Boolean);
  const contacts = [...(crm?.contacts || [])].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  const usageByTag = Object.fromEntries(tagIds.map((id) => [id, 1]));

  const handleDelete = async (contactId) => {
    try {
      await deleteContact({ clinicId: clinic.clinicId, contactId }).unwrap();
    } catch (error) {
      console.error("[CK-9817] Error deleting clinic contact from detail page:", {
        errorCode: "CK-9817",
        errorMessage: error?.message,
        clinicId: clinic.clinicId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה במחיקת הקשר");
    }
  };

  return (
    <section className="ncd2-card nivut-scope" id="ncd2-block-tags">
      <header className="ncd2-card-head">
        <span className="ncd2-card-icon">
          <Tag size={15} weight="bold" />
        </span>
        <div className="ncd2-card-titles">
          <span className="ncd2-card-eyebrow">מעקב</span>
          <h3 className="ncd2-card-title">תגיות וקשר</h3>
        </div>
      </header>

      <div className="ncd2-tags-row">
        {userTags.length === 0 && <span className="ncd2-muted ncd2-note">אין תגיות</span>}
        {userTags.map((tag) => (
          <UserTagChip key={tag.id} tag={tag} />
        ))}
        <span className="nivck2-pop-anchor">
          <button
            type="button"
            className="nivck2-tag-add"
            title="תגיות"
            aria-label="הוסף תגית"
            onClick={() => setPopover((p) => (p === "tags" ? null : "tags"))}
          >
            <Plus size={11} weight="bold" />
          </button>
          {popover === "tags" && (
            <TagAssignPopover
              clinicId={clinic.clinicId}
              tagIds={tagIds}
              tags={tags}
              onClose={() => setPopover(null)}
              onManage={() => setManagerOpen(true)}
            />
          )}
        </span>
      </div>

      <div className="ncd2-contact-head">
        <span className="ncd2-subhead">
          קשר אחרון: {crm?.lastContactAt ? `${getRelativeDate(crm.lastContactAt)} · ${formatDate(crm.lastContactAt)}` : "לא נרשם"}
        </span>
        <span className="nivck2-pop-anchor">
          <button
            type="button"
            className="ncd2-btn ncd2-btn-secondary ncd2-btn-sm"
            onClick={() => setPopover((p) => (p === "contact" ? null : "contact"))}
            aria-expanded={popover === "contact"}
          >
            <Plus size={13} weight="bold" /> רשום קשר
          </button>
          {popover === "contact" && (
            <ContactQuickLogPopover clinicId={clinic.clinicId} author={author} onClose={() => setPopover(null)} />
          )}
        </span>
      </div>

      {contacts.length === 0 ? (
        <div className="ncd2-empty">אין קשרים רשומים</div>
      ) : (
        <ul className="ncd2-contact-list">
          {contacts.map((c) => {
            const Icon = CHANNEL_ICONS[c.channel] || DotsThree;
            return (
              <li key={c.id} className="ncd2-contact-item">
                <span className={`ncd2-contact-icon ncd2-contact-icon-${c.channel || "other"}`}>
                  <Icon size={14} weight="fill" />
                </span>
                <div className="ncd2-contact-text">
                  <span className="ncd2-contact-meta">
                    <strong>{CONTACT_CHANNEL_LABELS[c.channel] || "אחר"}</strong>
                    {" · "}
                    {formatDate(c.date)}
                    {c.author ? ` · ${c.author}` : ""}
                  </span>
                  {c.note && <span className="ncd2-contact-note">{c.note}</span>}
                </div>
                <HoverTip text="מחק">
                  <button
                    type="button"
                    className="ncd2-icon-btn is-danger"
                    onClick={() => handleDelete(c.id)}
                    disabled={isDeleting}
                    aria-label="מחק קשר"
                  >
                    <Trash size={13} />
                  </button>
                </HoverTip>
              </li>
            );
          })}
        </ul>
      )}

      {managerOpen && <TagManagerModal tags={tags} usageByTag={usageByTag} onClose={() => setManagerOpen(false)} />}
    </section>
  );
}
